import type { EditorState, Transaction } from '@atlaskit/editor-prosemirror/state';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { closeHistory } from '@atlaskit/prosemirror-history/closeHistory';

import { configItemUsesRovo } from '../../../ui/components/SessionUI/utils/checkMigrationStatus';
import type {
	StepName,
	UserFlowStepAttributes,
} from '../../../ui/utils/analytics/analyticsFlowTypes';
import { isQuickActionConfigItem } from '../../agent-attribution/resolveAgentAttribution';
import { startWatchingAnnotationChanges } from '../../ai-annotation-watchmen/transforms';
import { aiSessionContextPluginKey } from '../../ai-session-context/ai-session-context-plugin-key';
import { aiExperienceDecorationPluginKey } from '../../decoration/decoration-plugin-key';
import {
	getFirstOutsideRunRange,
	isImproveDescriptionSyncBlockFixEnabled,
} from '../../improve-description-sync-block';
import { initPositions } from '../transforms';
import { validatePositions } from '../utils/positionValidation';
import { enclosingTextblockRange } from './slicesMatchingReplaceRange';

export type FireAnalyticsStep = (
	step: StepName,
	targetStep?: StepName,
	attributes?: Partial<UserFlowStepAttributes>,
) => void;

/**
 * Builds a transaction to initialize streaming decorations and state.
 * Returns null if:
 * - No positions or config item available
 * - Waiting for orchestrator to set correct positions (deferred mount)
 * - Unable to slice the original user content
 */
export function buildMountTransaction(
	state: EditorState,
	fireAnalyticsStep: FireAnalyticsStep,
): Transaction | null {
	const decorationPluginState = aiExperienceDecorationPluginKey.getState(state);
	const { positions, configItem, lastTriggeredFrom } = decorationPluginState ?? {};

	if (!positions || !configItem) {
		return null;
	}

	// Defer mounting for rovo prompts until we have the correct positions.
	// Exception: fallback free-gen (userInput is set) already has correct positions
	// from the initial decoration, so we can mount immediately.
	const sessionState = aiSessionContextPluginKey.getState(state);
	const isFallbackFreeGen = sessionState?.userInput !== undefined;

	if (
		configItemUsesRovo(configItem.key) &&
		lastTriggeredFrom &&
		lastTriggeredFrom !== 'streamingApi' &&
		!isFallbackFreeGen
	) {
		return null;
	}
	const [from, to] = positions;

	const validation = validatePositions(state, from, to);

	const docSize = state.doc.content.size;
	const isEmptySelection = from === to;
	const shouldReplaceDoc = isEmptySelection && !configItem?.insertOnEmptySelection;

	let firstOutsideRunRange: [number, number] | undefined;
	if (shouldReplaceDoc && isImproveDescriptionSyncBlockFixEnabled(configItem.key)) {
		firstOutsideRunRange = getFirstOutsideRunRange(state.doc);
		if (!firstOutsideRunRange) {
			return null;
		}
	}

	const [start, end] = shouldReplaceDoc
		? (firstOutsideRunRange ?? [1, Math.max(1, docSize - 1)])
		: [validation.sanitized.startPos, validation.sanitized.endPos];

	// Old non-unified free-gen path: telepointer.
	const showTelepointer =
		!isExperimentEnabled('platform_editor_ai_streaming_ux_experience_m1') &&
		!configItem?.streamsMarkdown;
	// Old non-unified free-gen path: faded original text.
	const showHighlight =
		!isExperimentEnabled('platform_editor_ai_streaming_ux_experience_m1') &&
		!configItem?.streamsMarkdown;
	// Old non-unified free-gen path: purple AI-content highlight.
	const showAIContentHighlight =
		!isExperimentEnabled('platform_editor_ai_streaming_ux_experience_m1') && !shouldReplaceDoc;

	// Same range restore / Review undo will replace. A closed whole-node slice
	// on an inner range splits the wrapper (empty heading, extra table cell).
	// Review Moment pairs the stored slice with the replace span at commit.
	// Keep a closed enclosing textblock for Review only — do not change the
	// live stream range (that leaves the original textblock in the document).
	let originalUserContentSlice;
	let reviewOriginalSlice;
	try {
		originalUserContentSlice = state.doc.slice(start, end);
		const snap = enclosingTextblockRange(state.doc, start, end);
		reviewOriginalSlice = snap ? state.doc.slice(snap.from, snap.to) : originalUserContentSlice;
	} catch {
		return null;
	}

	let telepointerPos = start;
	try {
		const $start = state.doc.resolve(start);
		telepointerPos = $start.pos;
		let node = $start.node()?.firstChild;
		while (node && node.firstChild && !node.isLeaf) {
			telepointerPos += 1;
			node = node.firstChild;
		}
	} catch {
		telepointerPos = start;
	}

	let tr = state.tr;
	tr = closeHistory(tr);

	tr = startWatchingAnnotationChanges({ from: start, to: end })(tr);

	tr = initPositions({
		start,
		end,
		showHighlight: showHighlight && decorationPluginState?.withStreamingHighlight !== false,
		showAIContentHighlight,
		showTelepointer: showTelepointer && decorationPluginState?.withStreamingHighlight !== false,
		isQuickAction: isQuickActionConfigItem(configItem.key),
		telepointerPos,
		originalUserContentSlice,
		reviewOriginalSlice,
		streamStartPos: start,
		sessionId: decorationPluginState?.currentSessionId,
	})(tr);

	fireAnalyticsStep('invoke', 'loading');

	return tr;
}
